import React, { useCallback } from "react";
import Act from "../../../../../store/actions";

/**
 * @typedef DragDefault {object}
 * @property x {number}
 * @property y {number}
 * @property width {number}
 * @property height {number}
 */

/**
 * @typedef DragSize {object}
 * @property width {number}
 * @property height {number}
 */

/**
 * @typedef DragPosition {object}
 * @property x {number}
 * @property y {number}
 */

/**
 * @typedef Bounds {object}
 * @property x {number}
 * @property y {number}
 * @property width {number}
 * @property height {number}
 * @property dragEles {string[]}
 */

const useDraggable = ({
  dragSize,
  setDragSize,
  dragPosition,
  setDragPosition,
  bounds,
  rndRef,
  dragEles,
  dispatch,
  intl,
  defaultSize,
  defaultMaxSize,
  memoSnaWidth,
  memoSnaHeight,
}) => {
  /**
   *
   * @type {import("react-rnd").RndResizeCallback}
   */
  const onResize = useCallback((e, dir, elementRef, delta, position) => {
    /**
     *
     * @param val {string | number}
     * @returns {number}
     */
    const pxToNumber = (val) =>
      Number.parseInt(String(val).replace("px", ""), 10);

    if (rndRef !== null) {
      rndRef.updateSize({
        // @ts-ignore
        width: pxToNumber(elementRef.style.width),
        // @ts-ignore
        height: pxToNumber(elementRef.style.height),
      });
    }
    setDragSize({
      // @ts-ignore
      width: pxToNumber(elementRef.style.width),
      // @ts-ignore
      height: pxToNumber(elementRef.style.height),
    });
  }, []);

  /**
   *
   * @param x {number}
   * @param _dragSize {DragSize}
   * @param _bounds {Bounds}
   * @returns {number|*}
   */
  const calcX = (x, _dragSize, _bounds) => {
    if (x < 0) return 0;
    if (x + _dragSize.width > _bounds.width)
      return _bounds.width - _dragSize.width;
    return x;
  };

  /**
   *
   * @param y {number}
   * @param _dragSize {DragSize}
   * @param _bounds {Bounds}
   * @returns {number|*}
   */
  const calcY = (y, _dragSize, _bounds) => {
    if (y < 0) return 0;
    if (y + _dragSize.height > _bounds.height)
      return _bounds.height - _dragSize.height;
    return y;
  };

  /**
   *
   * @type {import("react-rnd").RndDragCallback}
   */
  const onDrag = useCallback(
    (e, data) => {
      if (rndRef !== null) {
        rndRef.updatePosition({
          x: calcX(data.x, dragSize, bounds),
          y: calcY(data.y, dragSize, bounds),
        });
        setDragPosition({
          x: calcX(data.x, dragSize, bounds),
          y: calcY(data.y, dragSize, bounds),
        });
      }
    },
    [dragSize, bounds]
  );

  /**
   *
   * @type {import("react-rnd").RndDragCallback}
   */
  const onDragStop = useCallback(
    (e, data) => {
      if (rndRef !== null) {
        rndRef.updatePosition({
          x: calcX(data.x, dragSize, bounds),
          y: calcY(data.y, dragSize, bounds),
        });
        setDragPosition({
          x: calcX(data.x, dragSize, bounds),
          y: calcY(data.y, dragSize, bounds),
        });
      }
    },
    [dragEles, dragSize, bounds]
  );

  /**
   * 將拖曳視窗放到最小
   * @type {(function(): void)|*}
   */
  const setDragMiniSize = useCallback(() => {
    if (rndRef !== null) {
      rndRef.updateSize({
        ...defaultSize,
      });
      rndRef.updatePosition({
        x: memoSnaWidth - defaultSize.width * 2,
        y: memoSnaHeight - defaultSize.height,
      });
      setDragSize({
        ...defaultSize,
      });
      setDragPosition({
        x: memoSnaWidth - defaultSize.width * 2,
        y: memoSnaHeight - defaultSize.height,
      });
    }
  }, [rndRef, defaultSize, memoSnaWidth, memoSnaHeight]);

  /**
   * 將拖曳視窗放到最大
   * @type {(function(): void)|*}
   */
  const setDragMaxSize = useCallback(() => {
    if (rndRef !== null) {
      rndRef.updateSize({
        width: defaultMaxSize.width,
        height: defaultMaxSize.height,
      });
      rndRef.updatePosition({
        x: memoSnaWidth - defaultMaxSize.width,
        y: memoSnaHeight - defaultMaxSize.height,
      });
      setDragSize({
        width: defaultMaxSize.width,
        height: defaultMaxSize.height,
      });
      setDragPosition({
        x: memoSnaWidth - defaultMaxSize.width,
        y: memoSnaHeight - defaultMaxSize.height,
      });
    }
  }, [rndRef, defaultMaxSize, memoSnaWidth, memoSnaHeight]);

  /**
   *
   * @type {(function(): void)|*}
   */
  const askForWindowOpen = React.useCallback(() => {
    // 詢問是否開啟
    dispatch({
      type: Act.SNA_DIALOG_CONTEXT,
      payload: {
        openSignal: true,
        title: intl.formatMessage({
          id: "sna.main.displayArea.diaryInfo.display.window.question",
          defaultMessage: "是否開啟事件總攬視窗嗎？",
        }),
        contentText: "",
        onYes: () => {
          // 開啟視窗
          dispatch({
            type: Act.SNA_DIARY_INFO_DISPLAY_WINDOW,
            payload: true,
          });
        },
        yesText: intl.formatMessage({
          id: "common.yes",
          defaultMessage: "是",
        }),
        noText: intl.formatMessage({
          id: "common.no",
          defaultMessage: "否",
        }),
      },
    });
  }, [setDragMaxSize, intl, dispatch]);

  return {
    onDrag,
    onDragStop,
    onResize,
    askForWindowOpen,
    setDragMiniSize,
    setDragMaxSize,
  };
};

export default useDraggable;
